// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import Close from "@guestsupply/XDL/Icons/Close";
import { buttonTransparent } from "@guestsupply/XDL/PreStyle/button";
import { xBaseTheme } from "@guestsupply/XDL/types";
import { color, ellipsis, styledScrollbar, xScreen } from "@guestsupply/XDL/Utilities";
import { IconWrapper } from "@insite/mobius/Icon";
import { css } from "styled-components";

const MODAL_DESKTOP_PADDING = 20;
const MODAL_MOBILE_PADDING = 15;

export default {
    sizeVariants: { small: 280, medium: 420, large: 900 },
    defaultProps: {
        mergeCss: true,
        cssOverrides: {
            modalBody: css`
                overflow: hidden;
                border-radius: 4px;
                background: ${color("common.ui1")};
                &&&& {
                    box-shadow: inset 0 4px 0 0 ${color("primary")};
                }
                overflow: unset;

                //Delete this media query for a full-screen modal on mobile
                ${xScreen("lte", "md")} {
                    max-width: 90%;
                    height: auto;
                    max-height: calc(100% - 35px);
                    margin: 15px;
                }
            `,
            headlineTypography: css`
                margin-bottom: 0;
            `,
            modalContainer: css`
                &&&& {
                    align-items: center;
                }
            `,
            titleButton: css`
                &&&& {
                    ${buttonTransparent}
                    padding: 0;
                    position: absolute;
                    right: 20px;
                    top: 25px;
                    &:focus {
                        outline: none;
                    }

                    & > ${IconWrapper} {
                        position: unset;
                        top: 0;
                        color: ${color("common.ui6")};
                    }
                    &:hover:not(:disabled) {
                        ${buttonTransparent}
                        background-color: ${color("common.ui1")};
                        & > ${IconWrapper} {
                            color: ${color("common.ui6", 0.33)};
                        }
                    }
                }
            `,
            modalTitle: css`
                &&& {
                    min-height: 55px;
                    padding: ${MODAL_DESKTOP_PADDING}px ${MODAL_DESKTOP_PADDING}px ${MODAL_DESKTOP_PADDING / 2}px;
                    ${xScreen("lte", "xs")} {
                        padding-left: ${MODAL_MOBILE_PADDING}px;
                        padding-right: ${MODAL_MOBILE_PADDING}px;
                    }
                    & > h5 {
                        padding-right: 0;
                        margin-bottom: 0;
                        ${ellipsis(1)}
                    }
                }
            `,
            modalContent: css`
                &&& {
                    padding: ${MODAL_DESKTOP_PADDING / 2}px ${MODAL_DESKTOP_PADDING}px ${MODAL_DESKTOP_PADDING}px;
                }

                flex: 1;
                display: flex;
                flex-direction: column;

                ${styledScrollbar()}

                .modal-actions {
                    display: flex;
                    flex: 0 0 auto;
                    flex-wrap: wrap;
                    justify-content: flex-end;
                    width: 100%;
                    margin-top: 20px;

                    background: ${color("common.ui1")};

                    button {
                        margin: 0 0 10px 0;
                    }
                }

                ${xScreen("lte", "xs")} {
                    padding-left: ${MODAL_MOBILE_PADDING}px;
                    padding-right: ${MODAL_MOBILE_PADDING}px;

                    .modal-actions {
                        flex-direction: column;
                        justify-content: center;

                        button {
                            margin: 10px 0 0 0;
                            width: 100%;

                            &:first-child {
                                margin-top: 0;
                            }
                        }
                    }
                }
            `,
        },
        sizeVariant: "medium",
        headlineTypographyProps: {
            variant: "h5",
            css: css`
                margin-bottom: 0;
            `,
        },
        closeButtonProps: {
            buttonType: "solid",
            sizeVariant: "small",
            shape: "pill",
            variant: "primary",
            color: "common.ui1",
            size: 11,
        },
        closeButtonIconProps: {
            src: Close,
            size: 11,
            color: "common.ui6",
        },
    },
} as xBaseTheme["modal"];
